import React from 'react';
import { formatTableText } from '@/lib/tableUtils';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Plus, Scale, ClipboardList } from 'lucide-react';

const RakeReceiptsTable = ({
  trips,
  activeTab,
  canDispatch,
  isReadOnly,
  onOpenGateIn,
  onOpenCreateDispatch,
  onOpenAddMissingWagon,
}) => {
  const getVarianceBadge = (varianceType, varianceQty) => {
    const qty = Math.abs(parseFloat(varianceQty || 0)).toFixed(2);
    if (varianceType === 'EXCESS') {
      return (
        <Badge variant="outline" className="bg-amber-100 hover:bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 font-semibold border-amber-200">
          +{qty} MT
        </Badge>
      );
    } else if (varianceType === 'SHORTAGE') {
      return (
        <Badge variant="outline" className="bg-red-100 hover:bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 font-semibold border-red-200">
          -{qty} MT
        </Badge>
      );
    } else if (varianceType === 'BALANCED') {
      return (
        <Badge variant="outline" className="bg-emerald-100 hover:bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 font-semibold border-emerald-200">
          Balanced
        </Badge>
      );
    }
    return <Badge variant="outline">Pending</Badge>;
  };

  const showActions = (!isReadOnly && activeTab === 'pending') || (canDispatch && activeTab === 'received');

  return (
    <div className="rounded-md border overflow-hidden w-full">
      <div className="max-h-[calc(100vh-280px)] overflow-auto w-full">
        <Table>
          <TableHeader className="sticky top-0 z-10">
            <TableRow>
              <TableHead className="h-10 px-4 py-1">Trip Number</TableHead>
              <TableHead className="h-10 px-4 py-1">RR Number</TableHead>
              <TableHead className="h-10 px-4 py-1">Rake Point</TableHead>
              <TableHead className="h-10 px-4 py-1">Product</TableHead>
              <TableHead className="h-10 px-4 py-1">Baseline Qty</TableHead>
              <TableHead className="h-10 px-4 py-1">State</TableHead>
              {activeTab === 'received' && (
                <>
                  <TableHead className="h-10 px-4 py-1">Missing Wagons</TableHead>
                  <TableHead className="h-10 px-4 py-1">Dispatch Balance</TableHead>
                  <TableHead className="h-10 px-4 py-1">Variance</TableHead>
                </>
              )}
              {showActions && (
                <TableHead className="h-10 px-4 py-1 text-right min-w-[80px]">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {trips.map((trip) => (
              <TableRow key={trip.id} className="hover:bg-transparent border-border">
                <TableCell className="h-10 px-4 py-1 font-semibold text-primary">
                  {formatTableText(trip.trip_number)}
                </TableCell>
                <TableCell className="h-10 px-4 py-1">
                  {formatTableText(trip.rr_number)}
                </TableCell>
                <TableCell className="h-10 px-4 py-1">
                  {formatTableText(trip.rake_point_name || trip.rake_point?.name)}
                </TableCell>
                <TableCell className="h-10 px-4 py-1">
                  {formatTableText(trip.product_name)}
                </TableCell>
                <TableCell className="h-10 px-4 py-1">
                  {trip.baseline_qty_mt !== null && trip.baseline_qty_mt !== undefined
                    ? `${parseFloat(trip.baseline_qty_mt).toFixed(2)} MT`
                    : '—'}
                </TableCell>
                <TableCell className="h-10 px-4 py-1">
                  {formatTableText(trip.consignee_state || trip.distributor?.state_name)}
                </TableCell>

                {activeTab === 'received' && (
                  <>
                    <TableCell className="h-10 px-4 py-1 font-medium">
                      {trip.missing_wagons !== null && trip.missing_wagons !== undefined ? trip.missing_wagons : '0'}
                    </TableCell>
                    <TableCell className="h-10 px-4 py-1 whitespace-normal">
                      <div className="text-xs space-y-0.5 min-w-[240px]">
                        <div className="font-semibold text-emerald-600 dark:text-emerald-400">
                          Remaining Dispatch Balance: {parseFloat(trip.remaining_qty_mt !== undefined ? trip.remaining_qty_mt : (trip.received_qty_mt || trip.baseline_qty_mt || 0)).toFixed(2)} MT
                        </div>
                        <div className="text-[11px] text-muted-foreground font-normal">
                          Dispatched: {parseFloat(trip.dispatched_qty_mt || 0).toFixed(2)} MT / Received: {parseFloat(trip.received_qty_mt || trip.baseline_qty_mt || 0).toFixed(2)} MT
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="h-10 px-4 py-1">
                      {getVarianceBadge(trip.variance_type, trip.variance_qty_mt)}
                    </TableCell>
                  </>
                )}

                {showActions && (
                  <TableCell className="h-10 px-4 py-1 text-right whitespace-nowrap w-[80px]">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {activeTab === 'pending' && !isReadOnly && (
                          <DropdownMenuItem onClick={() => onOpenGateIn(trip)}>
                            <Scale className="mr-2 h-4 w-4" />
                            Log Gate-In
                          </DropdownMenuItem>
                        )}

                        {activeTab === 'received' && canDispatch && trip.can_create_secondary && (
                          <DropdownMenuItem
                            onClick={() => onOpenCreateDispatch(trip)}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Create Dispatch
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default RakeReceiptsTable;
